import { Button } from '@daily/shared/components/Button';
import { Text } from '@daily/shared/components/Text';
import { Tooltip } from '@daily/shared/components/Tooltip';
import { useTheme } from '@daily/shared/contexts/Theme';
import { pxToRem } from '@daily/shared/lib/pxToRem';
import { useDaily, useLocalSessionId } from '@daily-co/daily-react';
import classNames from 'classnames';
import { t } from 'i18next';
import { useRef } from 'react';

import {
  ChatMessage,
  defaultReactions,
  Reaction,
} from '/features/chat/contexts/ChatProvider';
import { hasReactedBefore } from '/features/chat/util/advancedChat';
interface Props {
  message: ChatMessage;
  onReact(reaction: Reaction): void;
}

const MAX_TOOLTIP_NAME_COUNT = 6;

export const ChatReactions: React.FC<React.PropsWithChildren<Props>> = ({
  message,
  onReact,
}) => {
  const { colors, isDarkMode } = useTheme();
  const reactionsRef = useRef<HTMLDivElement>(null);
  const daily = useDaily();

  const localSessionId = useLocalSessionId();

  const handleClick = (reaction: Reaction) => {
    onReact(reaction);
  };

  /**
   * Returns the tooltip title for a reaction button, based on a list of session_ids.
   * The component doesn't automatically re-render, in case participants change
   * their names. Instead, it will lazily update the names, as reactions are
   * added or removed for a message. This has the effect that temporarily names
   * might not be represented correctly until the component runs through the next
   * render cycle.
   * However, setting up listeners to detect name updates is relatively expensive
   * and could lead to many more re-renders than necessary.
   */
  const getTooltipTitle = (
    reaction: Reaction,
    ids: string[],
    amountOfReactions: number
  ) => {
    const containsLocalUser = ids.includes(localSessionId);
    const idsWithoutLocal = ids.filter((id) => id !== localSessionId);
    const participants = daily?.participants();

    const remoteIdsWithName = idsWithoutLocal.filter((id) =>
      Boolean(participants?.[id]?.user_name)
    );
    const max = MAX_TOOLTIP_NAME_COUNT - (containsLocalUser ? 1 : 0);
    const displayableNames = remoteIdsWithName.slice(0, max);
    const nonDisplayableNamesCount = Math.max(
      0,
      remoteIdsWithName.length - max
    );
    const withoutNameCount = idsWithoutLocal.length - remoteIdsWithName.length;

    const names = [
      ...(containsLocalUser ? [t('people.you')] : []),
      ...(displayableNames.length
        ? displayableNames.map((id) => participants[id].user_name)
        : []),
    ];

    if (names.length === 0 && withoutNameCount === 1) {
      names.push(t('people.someone'));
    } else if (withoutNameCount > 0 && nonDisplayableNamesCount === 0) {
      names.push(t('people.guestsWithCount', { count: withoutNameCount }));
    } else if (withoutNameCount > 0 || nonDisplayableNamesCount > 0) {
      names.push(
        t('people.othersWithCount', {
          count: nonDisplayableNamesCount + withoutNameCount,
        })
      );
    }

    return t('people.reactionsList', {
      names,
      reaction,
      count: amountOfReactions,
    });
  };

  return (
    <div className="chat-reactions" ref={reactionsRef}>
      {defaultReactions
        .filter((r) =>
          // filter out emojis that have at least one participantId
          Object.values(message.reactions?.[r] ?? {}).some(
            (ids) => ids.length > 0
          )
        )
        .map((reaction) => {
          // id of participants who responded with the reaction in question
          const reactions = Object.values(message.reactions?.[reaction] ?? {});
          const skinTonesWithReactions = Object.entries(
            message.reactions?.[reaction] ?? {}
          )
            .filter(([, ids]) => ids.length > 0)
            .map(([skinTone]) => skinTone);

          const hasReacted = hasReactedBefore(
            message.reactions?.[reaction],
            localSessionId
          );

          const count = reactions.reduce((curr, arr) => {
            return curr + arr.length;
          }, 0);

          const ids = reactions.reduce((curr, arr) => {
            return [...curr, ...arr];
          }, []);

          const modifiedEmoji = skinTonesWithReactions.join('');
          return (
            <Tooltip
              key={reaction}
              border
              id={`tt-${message.id}-${reaction}`}
              placement="bottom"
              style={{
                maxWidth: 220,
              }}
              title={getTooltipTitle(modifiedEmoji, ids, count)}
              tabIndex={-1}
            >
              <Button
                id={`chat-reaction-${reaction}-${message.id}`}
                className={classNames({
                  hasReacted,
                  local: message.local,
                })}
                onClick={() => handleClick(reaction)}
                variant="secondary"
                size="small"
              >
                <span className="emoji">{modifiedEmoji}</span>
                {count > 0 ? (
                  <Text className="count" color="inherit" El="span">
                    {count >= 100 ? '99+' : count}
                  </Text>
                ) : null}
              </Button>
            </Tooltip>
          );
        })}
      <style jsx>{`
        .chat-reactions {
          display: inline-flex;
          flex-wrap: wrap;
          gap: 4px;
        }
        .chat-reactions :global(button) {
          background: ${colors.background};
          display: flex;
          gap: 6px;
          justify-content: center;
          min-width: 24px;
          padding: 6px 8px;
          position: relative;
          z-index: 1;
        }
        .chat-reactions :global(button.hasReacted) {
          --blend: ${isDarkMode
            ? 'rgba(0, 0, 0, 0.3)'
            : 'rgba(255, 255, 255, 0.7)'};

          background: linear-gradient(0deg, var(--blend), var(--blend)),
            ${colors.accent};
          border-color: ${colors.accent};
          color: ${colors.accentText};
        }
        .chat-reactions .emoji {
          font-size: ${pxToRem(14)};
        }
        .chat-reactions :global(.count) {
          font-variant-numeric: tabular-nums;
        }
      `}</style>
    </div>
  );
};
